import axios, { AxiosRequestConfig, AxiosError, AxiosResponse, Method } from 'axios';
import jwt_decode, { JwtPayload } from "jwt-decode";

import i18n from 'i18next';
import { copyToClipboard, errorToaster, getFromLocal, setToLocal } from 'src/utils';

declare global {
    interface Window {
        API_URL: string;
        API_CLIENT_TYPE: string;
        TestHISKey: string;
        Integration_URL: string
    }
}

export const Host_URL: string = window.API_URL;
const Base_URL: string = Host_URL + "api";
const expirationMargin: number = 5 * 60 * 1000;
let refreshingTokenNow: boolean = false;

interface TokenResponse {
    data: {
        accessToken: string;
        refreshToken: string;
    };
}

interface ApiResponse<T = any> {
    data: T;
    status: number;
    statusText: string;
    headers: any;
    config: AxiosRequestConfig;
}

interface DecodedToken extends JwtPayload {
    exp: number;
}


interface AxiosErr extends AxiosError {
    response: AxiosResponse
}


export const APICall = async <T = any>(
    method: Method,
    API: string,
    params?: object,
    apiConfig: AxiosRequestConfig = {}
): Promise<ApiResponse<T>> => {
    const now = new Date();
    now.setTime(now.getTime() - expirationMargin);

    while (refreshingTokenNow) {
        await new Promise(resolve => setTimeout(resolve, 1000));
    }

    let token: DecodedToken | null = null;
    const accessToken = getFromLocal("accessToken");

    if (accessToken) {
        try {
            token = jwt_decode<DecodedToken>(accessToken);
        } catch (error) {
            console.error("Failed to decode token:", error);
        }
    }

    // Refresh token if expired
    if (token && new Date(token.exp * 1000) < now) {
        refreshingTokenNow = true;

        try {
            const refreshToken = getFromLocal("refreshToken");
            if (!accessToken || !refreshToken) {
                throw new Error("Missing tokens for refresh");
            }

            const response = await axios.post<TokenResponse>(
                Base_URL + "/auth/refresh",
                { accessToken, refreshToken },
                {
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Accept': 'application/octet-stream'
                    }
                }
            );

            setToLocal('accessToken', response.data.data.accessToken);
            setToLocal('refreshToken', response.data.data.refreshToken);
        } catch (error) {
            const axiosError = error as AxiosError;
            if (axiosError.response && [403, 401].includes(axiosError.response.status)) {
                forceLogout();
                throw axiosError;
            }
            throw error;
        } finally {
            refreshingTokenNow = false;
        }
    }

    try {
        const config: AxiosRequestConfig = {
            baseURL: Base_URL,
            method,
            url: `${API}${API.includes('?') ? "&" : "?"}culture=${i18n.language}`,
            data: params || undefined,
            responseType: API.includes('/excel') || API.includes('/pdf') || API.includes('/export') ? 'blob' : 'json',
            headers: {
                'Authorization': `Bearer ${getFromLocal("accessToken") || null}`,
                "X-Client-Type": window.API_CLIENT_TYPE,
                ...apiConfig.headers
            }
        };

        const response = await axios(config);
        return response;
    } catch (error) {
        const axiosError = error as AxiosErr;

        if (axiosError.response && [403, 401].includes(axiosError.response.status)) {
            forceLogout();
            throw axiosError;
        }
        const errorMessage = axiosError.response?.data?.message || axiosError.message;
        errorToaster(errorMessage);

        throw axiosError;
    }
};

export const forceLogout = (): void => {
    localStorage.clear();
    window.location.reload();
};

export * from './page';
